'use client'

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Cookie } from 'lucide-react'
import { Button } from '@/components/ui/button'

const CONSENT_KEY = 'dveriant-cookie-consent'

export function CookieConsent() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const checkConsent = () => {
      try {
        const stored = localStorage.getItem(CONSENT_KEY)
        return !stored
      } catch {
        return true
      }
    }
    if (checkConsent()) {
      const id = requestAnimationFrame(() => setVisible(true))
      return () => cancelAnimationFrame(id)
    }
  }, [])

  const handleAcceptAll = () => {
    try {
      localStorage.setItem(CONSENT_KEY, 'all')
    } catch {
      // ignore storage errors
    }
    setVisible(false)
  }

  const handleAcceptNecessary = () => {
    try {
      localStorage.setItem(CONSENT_KEY, 'necessary')
    } catch {
      // ignore storage errors
    }
    setVisible(false)
  }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', stiffness: 260, damping: 30 }}
          className="fixed bottom-0 left-0 right-0 z-40 border-t border-border/60 bg-background/80 backdrop-blur-xl dark:bg-background/60"
        >
          <div className="container mx-auto px-4 py-3 sm:py-4">
            {/* Desktop: horizontal bar layout */}
            <div className="hidden sm:flex sm:items-center sm:gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400">
                <Cookie className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold leading-tight">
                  Мы используем файлы cookie
                </h3>
                <p className="text-xs leading-relaxed text-muted-foreground mt-0.5">
                  Мы используем файлы cookie для улучшения работы сайта и персонализации. Продолжая пользоваться сайтом, вы соглашаетесь с нашей политикой cookie.
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  size="sm"
                  onClick={handleAcceptAll}
                  className="text-xs"
                >
                  Принять все
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleAcceptNecessary}
                  className="text-xs"
                >
                  Только необходимые
                </Button>
              </div>
            </div>

            {/* Mobile: compact stacked layout */}
            <div className="flex sm:hidden items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400">
                <Cookie className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xs font-semibold leading-tight">
                  Мы используем файлы cookie
                </h3>
                <p className="text-[11px] leading-relaxed text-muted-foreground mt-0.5">
                  Для улучшения работы сайта и персонализации.
                </p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0 pt-0.5">
                <Button
                  size="sm"
                  onClick={handleAcceptAll}
                  className="text-[11px] h-7 px-3"
                >
                  Принять
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleAcceptNecessary}
                  className="text-[11px] h-7 px-2"
                >
                  Минимум
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}